import { createColumnHelper } from '@tanstack/react-table'
import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import ActionMenu from '../components/ActionMenu'
import DateRangeFilter from '../components/DateRangeFilter'
import NotesPreview from '../components/NotesPreview'
import OpenMap from '../components/OpenMaps'
import PhoneContact from '../components/PhoneContact'
import SearchBar from '../components/SearchBar'
import SkeletonLoader from '../components/SkeletonLoader'
import StatusBadge from '../components/StatusBadge'
import CardRow from '../components/card/CardRow'
import Table from '../components/dashboard/Table'
import StatusReportConfirmationModal from '../components/report/StatusReportConfirmationModal'
import AddTagModal from '../components/tag/AddTagModal'
import ViewTagModal from '../components/tag/ViewTagModal'
import { useTagContext } from '../contexts/TagContext'
import { statusReportService } from '../services/statusReportService'
import {
  type TagFilters,
  getRelatedContractorName,
  tagService,
} from '../services/tagService'
import type { Tag } from '../types/tag'
import { formatDateTime } from '../utils/dateUtils'

// Removed driver-specific button config and actions

const formatPayRate = (rate: number, type: string | null): string => {
  return `$${rate.toFixed(2)}${
    type ? ` / ${type === 'HOURLY' ? 'hr' : 'project'}` : ''
  }`
}

const TagCard: React.FC<{ tag: Tag }> = ({ tag }) => {
  const [contractorName, setContractorName] = useState<string>('-')

  useEffect(() => {
    const fetchContractorName = async () => {
      const name = await getRelatedContractorName(tag)
      setContractorName(name)
    }
    fetchContractorName()
  }, [tag])

  const { date: startDate, time: startTime } = formatDateTime(tag.created_date)

  return (
    <div className={css(TagsDashboardStyles.card)}>
      <div className={css(TagsDashboardStyles.cardHeader)}>
        <h2 className={css(TagsDashboardStyles.cardTitle)}>Dispatch</h2>
        <StatusBadge status={tag.status} />
      </div>
      <div className={css(TagsDashboardStyles.cardBody)}>
        <CardRow label="TAG ID" value={tag.label} />
        <CardRow
          label="DISPATCHER"
          value={
            <PhoneContact
              displayName={
                `${tag.created_by.first_name || ''} ${
                  tag.created_by.last_name || ''
                }`.trim() || '-'
              }
              phoneNumber={tag.created_by.phone_number}
            />
          }
        />
        <CardRow label="CONTRACTOR" value={contractorName} />
        <CardRow label="START DATE" value={startDate} />
        <CardRow label="START TIME" value={startTime} />
        <CardRow label="MATERIAL" value={tag.material || '-'} />
        <CardRow
          label="PICK UP LOCATION"
          value={<OpenMap address={tag.origin_address || ''} />}
        />
        <CardRow
          label="DROP OFF LOCATION"
          value={<OpenMap address={tag.destination_address || ''} />}
        />
        {tag.notes && <CardRow label="NOTES" value={tag.notes} vertical />}
      </div>
    </div>
  )
}

const TagsDashboard: React.FC = () => {
  const { isOffline, refreshTags } = useTagContext()
  const [searchValue, setSearchValue] = useState<string>('')
  const [activeSearch, setActiveSearch] = useState<string>('')
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)
  const [viewingTag, setViewingTag] = useState<Tag | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const [pageTags, setPageTags] = useState<Tag[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined)

  const isDraftsRoute = location.pathname.includes('/tags/drafts')
  const isCompletedRoute = location.pathname.includes('/tags/completed')

  // Report mode state
  const [isReportMode, setIsReportMode] = useState(false)
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())
  const [showStatusReportConfirmation, setShowStatusReportConfirmation] =
    useState(false)
  const [selectedTagsForReport, setSelectedTagsForReport] = useState<Tag[]>([])
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  // Draft editing state
  const [editingDraft, setEditingDraft] = useState<Tag | null>(null)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fetchTags = useCallback(async () => {
    setIsLoading(true)
    try {
      const requestFilters: TagFilters & { page?: number; page_size?: number } =
        {
          page,
          page_size: pageSize,
        }

      if (activeSearch) {
        requestFilters.search = activeSearch
      }

      if (dateFrom) {
        requestFilters.start_after = dateFrom.toISOString()
      }

      if (dateTo) {
        // Set to end of day for the "to" date
        const endOfDay = new Date(dateTo)
        endOfDay.setHours(23, 59, 59, 999)
        requestFilters.start_before = endOfDay.toISOString()
      }

      // The route determines which statuses to show; filter server-side so
      // pagination counts reflect the visible set
      if (isDraftsRoute) {
        requestFilters.status = 'DRAFT'
      } else if (isCompletedRoute) {
        requestFilters.status = 'COMPLETE'
      } else {
        requestFilters.exclude_status = 'DRAFT,COMPLETE'
      }

      const res = await tagService.getTags(requestFilters)
      setPageTags(res.items)
      setTotalCount(res.count)
      setTotalPages(res.pages)
    } catch (error) {
      console.error('Failed to fetch tags:', error)
      toast.error('Failed to load tags')
    } finally {
      setIsLoading(false)
    }
  }, [
    activeSearch,
    dateFrom,
    dateTo,
    isDraftsRoute,
    isCompletedRoute,
    page,
    pageSize,
  ])

  useEffect(() => {
    fetchTags()
  }, [fetchTags])

  // Reset to the first page when filters or the route change
  // biome-ignore lint/correctness/useExhaustiveDependencies: page reset is intentional on filter/route change
  useEffect(() => {
    setPage(1)
  }, [activeSearch, dateFrom, dateTo, isDraftsRoute, isCompletedRoute])

  const handleSearch = () => {
    setActiveSearch(searchValue.trim())
  }

  const handleSearchClear = () => {
    setSearchValue('')
    setActiveSearch('')
  }

  const handleDateClear = () => {
    setDateFrom(null)
    setDateTo(null)
  }

  const handleViewTag = (tagId: string) => {
    navigate(`/tags/completed/${tagId}/confirmation`)
  }

  const handleViewNewTag = (tag: Tag) => {
    setViewingTag(tag)
  }

  // Draft handlers
  const handleContinueDraft = useCallback((tag: Tag) => {
    // Use tag data from the list directly - it's already fetched
    setEditingDraft(tag)
  }, [])

  const handleDeleteDraft = useCallback(
    async (tagId: string) => {
      if (!window.confirm('Are you sure you want to delete this draft?')) {
        return
      }
      try {
        await tagService.deleteDraft(tagId)
        toast.success('Draft deleted successfully')
        fetchTags()
      } catch (error) {
        console.error('Failed to delete draft:', error)
        toast.error('Failed to delete draft')
      }
    },
    [fetchTags],
  )

  const handleDraftModalClose = useCallback(() => {
    setEditingDraft(null)
    // Refresh both the visible page and the offline-capable context cache
    fetchTags()
    refreshTags(true)
  }, [fetchTags, refreshTags])

  // Report mode handlers
  const handleTagToggle = useCallback((tagId: string, checked: boolean) => {
    if (checked) {
      setSelectedTags((prev) => new Set([...Array.from(prev), tagId]))
    } else {
      setSelectedTags((prev) => {
        const newSet = new Set(prev)
        newSet.delete(tagId)
        return newSet
      })
    }
  }, [])

  const handleGenerateReport = useCallback(() => {
    setIsReportMode(true)
    setSelectedTags(new Set())
  }, [])

  const handleCancelReport = useCallback(() => {
    setIsReportMode(false)
    setSelectedTags(new Set())
  }, [])

  const handleSubmitReport = useCallback(() => {
    if (selectedTags.size === 0) {
      toast.error('Please select at least one tag to generate a report')
      return
    }

    // Get the selected tags data
    const selectedTagsData = pageTags.filter((tag) =>
      selectedTags.has(String(tag.id)),
    )
    setSelectedTagsForReport(selectedTagsData)
    setShowStatusReportConfirmation(true)
  }, [selectedTags, pageTags])

  const handleConfirmStatusReport = useCallback(
    async (recipientUserIds: number[], notes: string) => {
      setIsGeneratingReport(true)
      try {
        const tagIds = Array.from(selectedTags)
        const { report, statusReportId } =
          await statusReportService.generateReport({
            tag_ids: tagIds.map((id) => Number.parseInt(id)),
            recipient_user_ids:
              recipientUserIds.length > 0 ? recipientUserIds : null,
            notes: notes || null,
          })

        // Navigate to TagReportPage with the status report ID
        navigate(`/tag-report/${statusReportId}`)

        // Close the modal and reset state
        setShowStatusReportConfirmation(false)
        setIsReportMode(false)
        setSelectedTags(new Set())
      } catch (error) {
        console.error('Failed to generate report:', error)

        // Handle different error types
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { status?: number } }
          if (axiosError.response?.status === 405) {
            toast.error(
              'Report generation endpoint is not available yet. Please contact support.',
            )
          } else if (axiosError.response?.status === 403) {
            toast.error(
              'You do not have permission to generate reports. Please contact your administrator.',
            )
          } else if (axiosError.response?.status === 404) {
            toast.error(
              'Report generation service is not available. Please contact support.',
            )
          } else {
            toast.error('Failed to generate report. Please try again later.')
          }
        } else {
          toast.error('Failed to generate report. Please try again later.')
        }
      } finally {
        setIsGeneratingReport(false)
      }
    },
    [selectedTags, navigate],
  )

  const columnHelper = createColumnHelper<Tag>()

  const desktopColumns = [
    columnHelper.display({
      id: 'action',
      cell: (props) => {
        const tag = props.row.original
        const status = tag.status.toUpperCase()
        const isCompleted = status === 'COMPLETE'
        const isDraft = status === 'DRAFT'
        const isNew = status === 'NEW'
        const isCompletedRoute = location.pathname.includes('/tags/completed')
        const isDraftsRoute = location.pathname.includes('/tags/drafts')

        const actions = []

        // Draft-specific actions
        if (isDraft && isDraftsRoute) {
          actions.push({
            label: 'Continue Draft',
            icon: 'fa-solid fa-pen',
            onClick: () => handleContinueDraft(tag),
            className: css(TagsDashboardStyles.viewTagBtn),
          })
          actions.push({
            label: 'Delete Draft',
            icon: 'fa-solid fa-trash',
            onClick: () => handleDeleteDraft(String(tag.id)),
            className: css(TagsDashboardStyles.deleteBtn),
          })
        } else if (isCompleted && isCompletedRoute) {
          actions.push({
            label: 'View Tag',
            icon: 'fa-solid fa-eye',
            onClick: () => handleViewTag(String(tag.id)),
            className: css(TagsDashboardStyles.viewTagBtn),
          })
        } else if (
          !isCompleted &&
          !isDraft &&
          !isCompletedRoute &&
          !isDraftsRoute
        ) {
          if (isNew) {
            actions.push({
              label: 'View Tag',
              icon: 'fa-solid fa-eye',
              onClick: () => handleViewNewTag(tag),
              className: css(TagsDashboardStyles.viewTagBtn),
            })
          } else {
            actions.push({
              label: 'View Tag',
              icon: 'fa-solid fa-eye',
              onClick: () => {},
              className: css(
                TagsDashboardStyles.viewTagBtn,
                TagsDashboardStyles.viewTagBtnDisabled,
              ),
              disabled: true,
            })
          }
        }

        return actions.length > 0 ? <ActionMenu actions={actions} /> : null
      },
      header: () => <span>Action</span>,
    }),
    columnHelper.accessor('label', {
      cell: (info) => info.getValue(),
      header: () => <span>Tag ID</span>,
    }),
    columnHelper.accessor('title', {
      cell: (info) => info.getValue() || '-',
      header: () => <span>Title</span>,
    }),
    columnHelper.accessor('start_date', {
      id: 'start_date_date',
      cell: (info) => formatDateTime(info.getValue()).date,
      header: () => <span>Start Date</span>,
    }),
    columnHelper.accessor('start_date', {
      id: 'start_date_time',
      cell: (info) => {
        const { time } = formatDateTime(info.getValue())
        return time
      },
      header: () => <span>Start Time</span>,
    }),
    columnHelper.accessor('created_by', {
      cell: (info) => {
        const createdBy = info.getValue()
        return typeof createdBy === 'object' ? (
          <PhoneContact
            displayName={
              `${createdBy.first_name || ''} ${
                createdBy.last_name || ''
              }`.trim() || '-'
            }
            phoneNumber={createdBy.phone_number}
          />
        ) : (
          createdBy
        )
      },
      header: () => <span>Dispatcher</span>,
    }),
    columnHelper.display({
      id: 'driverName',
      cell: ({ row }) => {
        const tag = row.original
        // Try assigned_user first, then fall back to assigned_to.contact_object
        if (tag.assigned_user) {
          const fullName =
            `${tag.assigned_user.first_name || ''} ${tag.assigned_user.last_name || ''}`.trim()
          return fullName || tag.assigned_user.email || '-'
        }
        if (tag.assigned_to?.contact_object) {
          return (
            tag.assigned_to.contact_object.name ||
            tag.assigned_to.contact_object.email ||
            '-'
          )
        }
        return '-'
      },
      header: () => <span>Driver Name</span>,
    }),
    columnHelper.display({
      id: 'subHauler',
      cell: ({ row }) => {
        const tag = row.original
        // Get subhaulers from the workorder if it's an object with subhaulers
        if (
          typeof tag.workorder === 'object' &&
          tag.workorder &&
          'subhaulers' in tag.workorder &&
          tag.workorder.subhaulers &&
          tag.workorder.subhaulers.length > 0
        ) {
          return tag.workorder.subhaulers
            .map(
              (sh) =>
                `${sh.first_name || ''} ${sh.last_name || ''}`.trim() ||
                sh.email,
            )
            .join(', ')
        }
        return '-'
      },
      header: () => <span>Sub-Hauler</span>,
    }),
    columnHelper.display({
      id: 'projectId',
      cell: ({ row }) => {
        const tag = row.original
        if (typeof tag.project === 'object' && tag.project) {
          return (tag.project as { label?: string }).label || '-'
        }
        return '-'
      },
      header: () => <span>Project ID</span>,
    }),
    columnHelper.display({
      id: 'dispatchId',
      cell: ({ row }) => {
        const tag = row.original
        if (typeof tag.workorder === 'object' && tag.workorder) {
          return (tag.workorder as { label?: string }).label || '-'
        }
        return '-'
      },
      header: () => <span>Dispatch ID</span>,
    }),
    columnHelper.display({
      id: 'trucksNumber',
      cell: ({ row }) => {
        const tag = row.original
        if (typeof tag.workorder === 'object' && tag.workorder) {
          return (
            (tag.workorder as { id: number; trucks_number?: string })
              .trucks_number || '-'
          )
        }
        return '-'
      },
      header: () => <span>NUMBER OF TRUCKS</span>,
    }),
    columnHelper.accessor('status', {
      cell: (info) => <StatusBadge status={info.getValue()} />,
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor('material', {
      cell: (info) => {
        const material = info.getValue()
        return typeof material === 'object'
          ? JSON.stringify(material)
          : material
      },
      header: () => <span>Material</span>,
    }),
    columnHelper.accessor('origin_address', {
      cell: (info) => <OpenMap address={info.getValue() || ''} />,
      header: () => <span>Pick up location</span>,
    }),
    columnHelper.accessor('destination_address', {
      cell: (info) => <OpenMap address={info.getValue() || ''} />,
      header: () => <span>Drop off location</span>,
    }),
    columnHelper.accessor('notes', {
      cell: (info) => <NotesPreview note={info.getValue()} />,
      header: () => <span>Notes</span>,
    }),
  ]

  const mobileColumns = [
    columnHelper.display({
      id: 'action',
      cell: (props) => {
        const tag = props.row.original
        const status = tag.status.toUpperCase()
        const isCompleted = status === 'COMPLETE'
        const isNew = status === 'NEW'
        const isCompletedRoute = location.pathname.includes('/tags/completed')
        const actions = [] as {
          label: string
          icon: string
          onClick: () => void
          className?: string
          disabled?: boolean
        }[]
        if (isCompleted && isCompletedRoute) {
          actions.push({
            label: 'View',
            icon: 'fa-solid fa-eye',
            onClick: () => handleViewTag(String(tag.id)),
            className: css(TagsDashboardStyles.viewTagBtn),
          })
        } else if (!isCompleted && !isCompletedRoute) {
          if (isNew) {
            actions.push({
              label: 'View',
              icon: 'fa-solid fa-eye',
              onClick: () => handleViewNewTag(tag),
              className: css(TagsDashboardStyles.viewTagBtn),
            })
          } else {
            actions.push({
              label: 'View',
              icon: 'fa-solid fa-eye',
              onClick: () => {},
              className: css(
                TagsDashboardStyles.viewTagBtn,
                TagsDashboardStyles.viewTagBtnDisabled,
              ),
              disabled: true,
            })
          }
        }
        return actions.length > 0 ? <ActionMenu actions={actions} /> : null
      },
      header: () => <span>Action</span>,
    }),
    columnHelper.accessor('label', {
      cell: (info) => info.getValue(),
      header: () => <span>Tag</span>,
    }),
    columnHelper.accessor('start_date', {
      cell: (info) => {
        const { date, time } = formatDateTime(info.getValue())
        return `${date} ${time}`
      },
      header: () => <span>Start</span>,
    }),
    columnHelper.accessor('created_by', {
      cell: (info) => {
        const createdBy = info.getValue()
        return typeof createdBy === 'object'
          ? `${createdBy.first_name || ''} ${
              createdBy.last_name || ''
            }`.trim() || '-'
          : createdBy
      },
      header: () => <span>Dispatcher</span>,
    }),
    columnHelper.accessor('status', {
      cell: (info) => <StatusBadge status={info.getValue()} />,
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor('notes', {
      cell: (info) => <NotesPreview note={info.getValue()} />,
      header: () => <span>Notes</span>,
    }),
  ]

  const columns = desktopColumns

  return (
    <div className={css(TagsDashboardStyles.tagsDashboard)}>
      <div className={css(TagsDashboardStyles.mainContent)}>
        {isOffline && (
          <div className={css(TagsDashboardStyles.offlineIndicator)}>
            You are offline. Changes will sync when you're back online.
          </div>
        )}
        <div className={css(TagsDashboardStyles.header)}>
          <div className={css(TagsDashboardStyles.topRow)}>
            <div className={css(TagsDashboardStyles.titleSection)}>
              <h2
                className={css(
                  TagsDashboardStyles.title,
                  TagsDashboardStyles.dispatchTitle,
                )}
              >
                {location.pathname.includes('/tags/drafts')
                  ? 'Draft Tags'
                  : location.pathname.includes('/tags/completed')
                    ? 'Completed Tags'
                    : 'Open Tags'}
              </h2>
            </div>

            {!isReportMode && location.pathname.includes('/tags/completed') && (
              <div className={css(TagsDashboardStyles.actionButtons)}>
                <button
                  className={css(TagsDashboardStyles.generateReportButton)}
                  onClick={handleGenerateReport}
                >
                  Generate Report
                </button>
              </div>
            )}

            {isReportMode && (
              <div className={css(TagsDashboardStyles.reportModeActions)}>
                <span className={css(TagsDashboardStyles.selectedCount)}>
                  {selectedTags.size} tag{selectedTags.size !== 1 ? 's' : ''}{' '}
                  selected
                </span>
                <button
                  className={css(
                    TagsDashboardStyles.submitReportButton,
                    selectedTags.size === 0 &&
                      TagsDashboardStyles.submitReportButtonDisabled,
                  )}
                  onClick={handleSubmitReport}
                  disabled={selectedTags.size === 0}
                >
                  View Report
                </button>
                <button
                  className={css(TagsDashboardStyles.cancelReportButton)}
                  onClick={handleCancelReport}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className={css(TagsDashboardStyles.filtersRow)}>
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
              onClear={handleDateClear}
              showYearMonthDropdowns={true}
            />
            <SearchBar
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onSearch={handleSearch}
              onClear={handleSearchClear}
              placeholder="Search dispatches..."
            />
          </div>
        </div>

        <div className={css(TagsDashboardStyles.tableContainer)}>
          <div className={css(TagsDashboardStyles.tableWrapper)}>
            {isLoading ? (
              <SkeletonLoader
                rows={8}
                columns={6}
                showHeader={true}
                className={css(TagsDashboardStyles.skeletonLoader)}
              />
            ) : (
              <Table
                data={pageTags}
                columns={columns}
                page={page}
                pages={totalPages}
                count={totalCount}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
                isReportMode={isReportMode}
                selectedItems={selectedTags}
                onItemToggle={handleTagToggle}
                getItemId={(tag) => tag.id?.toString() || ''}
              />
            )}
          </div>
        </div>
      </div>

      {viewingTag && (
        <ViewTagModal tag={viewingTag} onClose={() => setViewingTag(null)} />
      )}

      {showStatusReportConfirmation && (
        <StatusReportConfirmationModal
          isOpen={showStatusReportConfirmation}
          completedTags={selectedTagsForReport}
          onClose={() => setShowStatusReportConfirmation(false)}
          onConfirm={handleConfirmStatusReport}
          isSubmitting={isGeneratingReport}
          suggestedRecipientIds={[
            ...new Set(
              selectedTagsForReport
                .filter((t) => t.contractor_user?.id)
                .map((t) => t.contractor_user?.id),
            ),
          ]}
        />
      )}

      {editingDraft && (
        <AddTagModal
          editingTag={editingDraft}
          onClose={handleDraftModalClose}
        />
      )}
    </div>
  )
}

const TagsDashboardStyles = StyleSheet.create({
  tagsDashboard: {
    display: 'flex',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    width: 'calc(100vw - 280px)',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    '@media (max-width: 1024px)': {
      padding: '12px',
      width: '100vw',
    },
  },
  mainContent: {
    flex: 1,
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: 20,

    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '16px',
      padding: '12px',
    },
  },
  title: {
    margin: 0,
    '@media (max-width: 1024px)': {
      fontSize: '1.5rem',
    },
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  generateReportButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  reportModeActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectedCount: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: 500,
  },
  submitReportButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  submitReportButtonDisabled: {
    backgroundColor: '#D1D5DB',
    color: '#9CA3AF',
    cursor: 'not-allowed',
    opacity: 0.6,
    ':hover': {
      backgroundColor: '#D1D5DB',
    },
  },
  cancelReportButton: {
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#4B5563',
    },
  },
  filtersRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '12px',
    },
  },
  desktopView: {
    '@media (max-width: 1024px)': {
      display: 'none',
    },
  },
  mobileView: {
    display: 'none',
    '@media (max-width: 1024px)': {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '16px',
    },
  },
  tableContainer: {
    borderRadius: '8px',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '-20px',
    backgroundColor: '#F9FAFB',
    overflowX: 'hidden',
  },
  tableWrapper: {
    width: '100%',
    backgroundColor: '#F9FAFB',
    display: 'block',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #E5E7EB',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  tagId: {
    fontWeight: 600,
    color: '#111827',
    fontSize: '12px',
  },
  actionColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: 16,
  },
  fillOutBtn: {
    padding: '10px 20px',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 8,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minWidth: '160px',
    whiteSpace: 'nowrap',
  },
  rejectBtn: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#EF4444',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 8,
    fontWeight: 500,
    width: '100%',
    textAlign: 'center',
    marginLeft: '7px',
    fontSize: '14px',
  },
  acceptDispatchBtn: {
    backgroundColor: '#10B981',
  },
  startBtn: {
    color: '#4B5563',
    background: '#FDE68A',
  },
  continueBtn: {
    backgroundColor: '#10B981',
  },
  viewDetailsBtn: {
    color: '#E1EFFE',
    background: '#1C64F2',
  },
  reviewTagBtn: {
    color: '#FFFFFF',
    background: '#7C3AED',
  },
  fillOutIcon: {
    marginRight: 8,
  },
  syncIndicator: {
    position: 'fixed',
    top: '16px',
    right: '16px',
    padding: '8px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    borderRadius: '4px',
    fontSize: '14px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#6B7280',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '16px',
  },
  skeletonLoader: {
    margin: '0',
    border: 'none',
    borderRadius: '8px',
  },
  offlineIndicator: {
    position: 'fixed',
    top: '16px',
    right: '16px',
    padding: '8px 16px',
    backgroundColor: '#FEF3C7',
    color: '#92400E',
    borderRadius: '8px',
    fontSize: '14px',
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    border: '1px solid #FCD34D',
    ':before': {
      content: '""',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#92400E',
    },
  },

  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },

  notesSection: {
    marginTop: '16px',
  },
  notesLabel: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '4px',
  },
  notesField: {
    padding: '12px',
    backgroundColor: '#F9FAFB',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
    fontSize: '14px',
    color: '#111827',
    minHeight: '200px',
  },
  cardTitle: {
    margin: 0,
    textAlign: 'left',
    fontSize: '1.2rem',
    fontWeight: 600,
    color: '#111827',
  },
  dispatchTitle: {
    fontSize: '1.4rem',
    fontWeight: 600,
  },
  viewTagBtn: {
    padding: '6px 12px',
    color: '#FFFFFF',
    background: '#7C3AED',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    maxWidth: '120px',
  },
  viewTagBtnDisabled: {
    background: '#9CA3AF',
    color: '#6B7280',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  viewTagIcon: {
    marginRight: 4,
    fontSize: '10px',
  },
  deleteBtn: {
    padding: '6px 12px',
    color: '#FFFFFF',
    background: '#EF4444',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    maxWidth: '120px',
    ':hover': {
      background: '#DC2626',
    },
  },
})

export default TagsDashboard
